import CreditMemo from "../../../classes/CreditMemo";
import { db } from "../../../boot/firebase";
import { deleteField, doc, updateDoc, writeBatch } from "firebase/firestore";
import { call, roundPrice } from "../../../util/misc";
import { forEach, omit } from "lodash";

export const GET_CREDIT_MEMO = (
  { dispatch },
  { projectID, saleID, creditMemoID }
) => {
  return dispatch("GET_DOC", {
    converter: CreditMemo,
    path: CreditMemo.getPath(projectID, saleID, creditMemoID),
    stateKey: "creditMemos",
    id: creditMemoID,
  });
};

export const LISTEN_CREDIT_MEMOS = (
  { dispatch },
  { projectID, saleID, listenerPath, next, error, unlisten }
) => {
  const path =
    projectID && saleID ? CreditMemo.getPath(projectID, saleID) : null;
  return dispatch("LISTEN_COLLECTION", {
    converter: CreditMemo,
    stateKey: "creditMemos",
    path,
    listenerPath,
    where: ["creditMemo", "==", true],
    next,
    error,
    unlisten,
  });
};

export const SAVE_CREDIT_MEMO = async (
  { dispatch, state },
  { projectID, saleID, creditMemo }
) => {
  const batch = writeBatch(db);

  const updateSale = async (changes = {}) => {
    return dispatch("UPDATE_SALE_STATUS", {
      batch,
      projectID,
      saleID,
      changes: {
        ...changes,
      },
    });
  };

  if (!creditMemo.id) {
    // New Credit Memo
    const now = new Date();
    creditMemo.data.created = now;
    creditMemo.data.createdBy = state.user.id;

    // Get unique incremental ID
    creditMemo.id = await call("getCreditMemoID");

    // Save Credit Memo
    batch.set(doc(db, creditMemo.path).withConverter(CreditMemo), creditMemo);

    await updateSale();
    await batch.commit();
  } else {
    // Existing Credit Memo
    creditMemo.data.modified = new Date();
    creditMemo.data.modifiedBy = state.user.id;
    const current = await dispatch("GET_CREDIT_MEMO", {
      projectID,
      saleID,
      creditMemoID: creditMemo.id,
    });

    // Save Credit Memo. The buffer is a copy taken when the dialog opened,
    // and computeTotal rebuilds `total` from scratch, so it can't be trusted
    // with what other writers own: `allocation` (touchInvoice), `locked`,
    // `total.payments` and `total.balance` (refunds, via addToBalance), and
    // `voidedDate` / `voidedReason` (VOID_CREDIT_MEMO). Update only the rest,
    // and carry the refunds already posted onto the new price.
    const changes = omit(creditMemo.data, [
      "allocation",
      "locked",
      "total",
      "voidedDate",
      "voidedReason",
    ]);
    forEach(
      omit(creditMemo.data.total, ["payments", "balance"]),
      (value, key) => {
        changes[`total.${key}`] = value;
      }
    );
    changes["total.balance"] = roundPrice(
      creditMemo.data.total.price - (current.data.total.payments || 0)
    );
    batch.update(doc(db, creditMemo.path), changes);

    await updateSale();
    await batch.commit();
  }
  return creditMemo;
};

export const VOID_CREDIT_MEMO = async (
  { dispatch, state },
  { projectID, saleID, creditMemoID, reason }
) => {
  const creditMemoRef = doc(
    db,
    CreditMemo.getPath(projectID, saleID, creditMemoID)
  );
  await updateDoc(creditMemoRef, {
    voidedDate: new Date(),
    voidedReason: reason || "",
    modified: new Date(),
    modifiedBy: state.user.id,
  });
  const batch = writeBatch(db);
  await dispatch("UPDATE_SALE_STATUS", {
    batch,
    projectID,
    saleID,
    changes: {
      [`status.invoices.${creditMemoID}`]: deleteField(),
    },
  });
  return batch.commit();
};

export const DELETE_CREDIT_MEMO = async (
  { dispatch },
  { projectID, saleID, creditMemoID }
) => {
  const batch = writeBatch(db);
  batch.delete(doc(db, CreditMemo.getPath(projectID, saleID, creditMemoID)));
  await dispatch("UPDATE_SALE_STATUS", {
    batch,
    projectID,
    saleID,
    changes: {
      [`status.invoices.${creditMemoID}`]: deleteField(),
    },
  });
  return batch.commit();
};
